import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.jpg";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";

interface PromoVideo {
  id: string;
  title_gu: string;
  title_hi: string | null;
  description_gu: string | null;
  description_hi: string | null;
  yt_video_id: string;
  video_language: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export default function AdminPromoVideos() {
  const [videos, setVideos] = useState<PromoVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<PromoVideo | null>(null);
  const [formData, setFormData] = useState({
    title_gu: "",
    title_hi: "",
    description_gu: "",
    description_hi: "",
    yt_video_id: "",
    video_language: "gu",
    display_order: 0,
    is_active: true,
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuth();
    fetchPromoVideos();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (!roles) {
      navigate("/");
    }
  };

  const fetchPromoVideos = async () => {
    try {
      const { data, error } = await supabase
        .from("promo_videos")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setVideos(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching promo videos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (video?: PromoVideo) => {
    if (video) {
      setEditingVideo(video);
      setFormData({
        title_gu: video.title_gu,
        title_hi: video.title_hi || "",
        description_gu: video.description_gu || "",
        description_hi: video.description_hi || "",
        yt_video_id: video.yt_video_id,
        video_language: video.video_language,
        display_order: video.display_order,
        is_active: video.is_active,
      });
    } else {
      setEditingVideo(null);
      setFormData({
        title_gu: "",
        title_hi: "",
        description_gu: "",
        description_hi: "",
        yt_video_id: "",
        video_language: "gu",
        display_order: videos.length,
        is_active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingVideo) {
        const { error } = await supabase
          .from("promo_videos")
          .update(formData)
          .eq("id", editingVideo.id);

        if (error) throw error;

        toast({
          title: "Promo video updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("promo_videos")
          .insert([formData]);

        if (error) throw error;

        toast({
          title: "Promo video created successfully",
        });
      }

      setIsDialogOpen(false);
      fetchPromoVideos();
    } catch (error: any) {
      toast({
        title: "Error saving promo video",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this promo video?")) return;

    try {
      const { error } = await supabase
        .from("promo_videos")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Promo video deleted successfully",
      });

      fetchPromoVideos();
    } catch (error: any) {
      toast({
        title: "Error deleting promo video",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (video: PromoVideo) => {
    try {
      const { error } = await supabase
        .from("promo_videos")
        .update({ is_active: !video.is_active })
        .eq("id", video.id);

      if (error) throw error;

      toast({
        title: `Promo video ${!video.is_active ? "activated" : "deactivated"}`,
      });

      fetchPromoVideos();
    } catch (error: any) {
      toast({
        title: "Error updating promo video",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Admin" className="h-10" />
              <h1 className="text-xl font-bold text-primary">Promo Videos Management</h1>
            </div>
            <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto p-8">
        <div className="space-y-6">
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Promo Video
          </Button>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <div className="bg-card rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Title (Gujarati)</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead>Video ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {videos.map((video) => (
                    <TableRow key={video.id}>
                      <TableCell>{video.display_order}</TableCell>
                      <TableCell className="font-medium">{video.title_gu}</TableCell>
                      <TableCell>{video.video_language.toUpperCase()}</TableCell>
                      <TableCell>{video.yt_video_id}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            video.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {video.is_active ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(video)}
                        >
                          {video.is_active ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(video)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(video.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVideo ? "Edit Promo Video" : "Add New Promo Video"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title_gu">Title (Gujarati) *</Label>
                <Input
                  id="title_gu"
                  value={formData.title_gu}
                  onChange={(e) =>
                    setFormData({ ...formData, title_gu: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title_hi">Title (Hindi)</Label>
                <Input
                  id="title_hi"
                  value={formData.title_hi}
                  onChange={(e) =>
                    setFormData({ ...formData, title_hi: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description_gu">Description (Gujarati)</Label>
              <Textarea
                id="description_gu"
                value={formData.description_gu}
                onChange={(e) =>
                  setFormData({ ...formData, description_gu: e.target.value })
                }
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description_hi">Description (Hindi)</Label>
              <Textarea
                id="description_hi"
                value={formData.description_hi}
                onChange={(e) =>
                  setFormData({ ...formData, description_hi: e.target.value })
                }
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="yt_video_id">YouTube Video ID *</Label>
                <Input
                  id="yt_video_id"
                  value={formData.yt_video_id}
                  onChange={(e) =>
                    setFormData({ ...formData, yt_video_id: e.target.value })
                  }
                  required
                  placeholder="jDnKZNHb7gA or full YouTube URL"
                />
                <p className="text-xs text-muted-foreground">
                  Enter video ID (e.g., jDnKZNHb7gA) or full YouTube URL - both formats work
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="video_language">Video Language *</Label>
                <Select
                  value={formData.video_language}
                  onValueChange={(value) =>
                    setFormData({ ...formData, video_language: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gu">Gujarati</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      display_order: parseInt(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingVideo ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
